import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { addCommentOnCaseDraft } from "services/caseServices";
import { CaseCreation, Roles } from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  selectedCase: CaseCreation;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const FeedbackSection: React.FC<Props> = ({ selectedCase, setRefetch }) => {
  const statusLabels: Record<string, string> = {
    respond_back: "Respond Back",
    pending: "Pending",
    forwarded_to_coordinator: "Forwarded to Coordinator",
    forwarded_to_registrar: "Forwarded to Registrar",
    sent_to_court: "Sent to Court",
  };

  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isDirector = hasRole(loginData?.roles, [Roles.prosecutionDirector]);
  const [newComment, setNewComment] = React.useState<string>("");
  const [orderToStop, setOrderToStop] = React.useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            Feedback Section
          </h3>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {selectedCase?.comments && selectedCase?.comments.length > 0 ? (
            selectedCase.comments.map((comment, index) => (
              <div
                key={index}
                className={`bg-white rounded-lg border p-4 ${
                  comment?.is_director_order_to_stop
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="font-semibold text-blue-600">
                        {comment.created_by?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {comment.created_by}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {comment?.created_at &&
                            new Date(comment?.created_at).toLocaleString()}
                        </span>
                        {comment?.status ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {statusLabels[comment.status?.toLowerCase()] ||
                              comment.status
                                ?.replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed">
                      {comment?.comment}
                    </p>

                    {comment?.is_director_order_to_stop && (
                      <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold ring-1 ring-red-300 animate-pulse">
                        <span className="animate-bounce inline-block">⚠️</span>
                        {t("director_order_to_stop")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t("no_feedback_yet")}
            </div>
          )}
        </div>

        {!selectedCase?.is_article_of_cahrge_changed &&
          selectedCase?.status !== "case_terminated_by_court" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">
                {t("add_feedback")}
              </h4>

              {isDirector && (
                <div
                  className={`mb-4 rounded-lg border p-3 transition-colors ${
                    orderToStop
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={orderToStop}
                      onChange={(e) => setOrderToStop(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-red-600 rounded focus:ring-red-500"
                    />
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        ⚠️ {t("order_to_stop")}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {t("order_to_stop_hint")}
                      </span>
                    </span>
                  </label>
                </div>
              )}

              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                {t("feedback")}
              </label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder={t("write_feedback_placeholder")}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />

              <div className="flex justify-end mt-4">
                <button
                  className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  onClick={() => {
                    if (!newComment.trim()) {
                      showTooltip(t("please_enter_a_comment"), "error");
                      return;
                    }

                    addCommentOnCaseDraft({
                      case: selectedCase?.id,
                      role: isCoordinator
                        ? "coordinator"
                        : isDirector
                          ? "director"
                          : "deputy",
                      comment: newComment,
                      id: 0,
                      created_by: loginData?.full_name,
                      is_director_order_to_stop: orderToStop,
                    })
                      .then(() => {
                        setNewComment("");
                        setOrderToStop(false);
                        setRefetch((prev: any) => !prev);
                        showTooltip(t("comment_added_successfully"), "success");
                      })
                      .catch((error) => {
                        showTooltip(
                          errorHandlerOnForm(error) || "Error adding comment:",
                          "error",
                        );
                      });
                  }}
                >
                  {t("send_feedback")}
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default FeedbackSection;
